/* eslint-disable camelcase */
import React, { useContext, useEffect, useRef, useState } from 'react';

import {
  useSegmentAnalytics,
  getDeployableInfo,
} from '@nerdwallet/seo-analytics';
import useIsVisible from './useIsVisible';
import { trackFormImpression } from './trackers';

type SegmentImpressionContextType = {
  eventId: string;
};

interface SegmentImpressionProps {
  children: React.ReactNode;
  component?: React.ElementType;
  entityName: string;
  formName: string;
}

const SegmentImpressionContext =
  React.createContext<SegmentImpressionContextType | null>(null);

const useSegmentImpressionContext = (): SegmentImpressionContextType => {
  const context = useContext<SegmentImpressionContextType>(
    SegmentImpressionContext as React.Context<SegmentImpressionContextType>
  );
  return context;
};

const hashEvent = (entityName, formName) => entityName + formName;

const impressionsMap = {};
const SegmentImpression: React.FC<SegmentImpressionProps> = ({
  children,
  component: Component = 'div',
  entityName,
  formName,
}) => {
  const segment = useSegmentAnalytics()?.analytics;

  // Track if the element is visible
  const ref = useRef(null);
  const { isVisible, registerElement } = useIsVisible();

  // Track the event id to avoid duplicate events
  const evtHash = hashEvent(entityName, formName);
  const [eventId, setEventId] = useState(null);

  // Track when the form is visible
  useEffect(() => {
    if (eventId || !segment || !isVisible) return;

    // Already tracked this impression?
    if (impressionsMap[evtHash]) {
      setEventId(impressionsMap[evtHash]);
    }

    const { deployable_name, deployable_version } = getDeployableInfo();

    const id = trackFormImpression(
      {
        entityName,
        deployable_name,
        deployable_version,
        formName,
      },
      segment
    );

    setEventId(id);
  }, [isVisible, entityName, formName, segment, eventId, setEventId, evtHash]);

  useEffect(() => {
    if (eventId) {
      impressionsMap[evtHash] = eventId;
    }
  }, [eventId, evtHash]);

  useEffect(() => {
    if (ref.current) {
      registerElement(ref.current);
    }
  }, [registerElement]);

  return (
    <SegmentImpressionContext.Provider value={{ eventId }}>
      <Component ref={ref}>{children}</Component>
    </SegmentImpressionContext.Provider>
  );
};

export { useSegmentImpressionContext, SegmentImpressionContext };
export default SegmentImpression;
